import React from 'react';

// Format currency as Indian Rupees
function formatCurrency(value) {
    if (!value && value !== 0) return '\u20B90';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(value);
}

// Format date as "Mon DD"
function formatShortDate(dateString) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
        return '-';
    }
}

// Calculate days since a date
function daysSince(dateString) {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function DealCard({ deal, onDragStart, onClick, isDragging, isBusy = false, disableDrag = false }) {
    const handleDragStart = (e) => {
        onDragStart?.(e, deal);
    };

    const handleClick = () => {
        onClick?.(deal);
    };

    const daysInStage = daysSince(deal.stageEnteredAt || deal.updatedAt || deal.createdAt);
    const nextActionAt = deal.nextActionAt ? new Date(deal.nextActionAt) : null;
    const isOverdue = nextActionAt ? nextActionAt.getTime() < Date.now() && !['won', 'lost'].includes(deal.stage) : false;

    const getDaysColor = () => {
        if (daysInStage > 14) return { background: 'var(--crm-danger-light)', color: 'var(--crm-danger)' };
        if (daysInStage > 7) return { background: 'var(--crm-warning-light)', color: '#b45309' };
        return { background: 'var(--crm-gray-100)', color: 'var(--crm-gray-600)' };
    };

    return (
        <div
            className={`crm-deal-card ${isDragging ? 'dragging' : ''} ${isBusy ? 'busy' : ''}`}
            draggable={!disableDrag}
            onDragStart={handleDragStart}
            onClick={handleClick}
        >
            {isBusy && (
                <div className="crm-deal-card-busy-indicator">
                    <span className="crm-spinner crm-spinner-inline" />
                    <span>Saving...</span>
                </div>
            )}
            <div className="crm-deal-card-title">{deal.title || 'Untitled Deal'}</div>

            {deal.contactName && (
                <div className="crm-deal-card-contact">
                    <span className="crm-deal-card-contact-name">
                        <svg className="crm-deal-card-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>{deal.contactName}</span>
                    </span>
                    {deal.contactPhone && (
                        <a
                            href={`tel:${deal.contactPhone}`}
                            className="crm-icon-btn"
                            onClick={(e) => e.stopPropagation()}
                            title="Call"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                        </a>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {deal.propertyName && (
                    <span className="crm-badge" style={{ background: 'var(--crm-gray-100)', color: 'var(--crm-gray-700)' }}>
                        {deal.propertyName}
                    </span>
                )}
                {deal.projectName && (
                    <span className="crm-badge" style={{ background: 'var(--crm-gray-100)', color: 'var(--crm-gray-700)' }}>
                        {deal.projectName}
                    </span>
                )}
                {deal.propertyType && (
                    <span className="crm-badge" style={{ background: 'var(--crm-gray-100)', color: 'var(--crm-gray-700)' }}>
                        {deal.propertyType}
                    </span>
                )}
            </div>

            <div className="crm-deal-card-value">{formatCurrency(deal.value)}</div>

            {deal.nextActionAt && (
                <div className="crm-deal-card-next">
                    <span
                        className="crm-badge"
                        style={{
                            background: isOverdue ? 'var(--crm-danger-light)' : 'var(--crm-gray-100)',
                            color: isOverdue ? 'var(--crm-danger)' : 'var(--crm-gray-700)'
                        }}
                    >
                        {deal.nextActionType || 'next'} {'\u2022'} {formatShortDate(deal.nextActionAt)}
                    </span>
                </div>
            )}

            <div className="crm-deal-card-footer">
                <div className="crm-deal-card-date">
                    <svg className="crm-deal-card-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>{formatShortDate(deal.expectedCloseDate)}</span>
                </div>

                <div className="crm-deal-card-footer-right">
                    <div className="crm-deal-card-updated">
                        Updated {formatShortDate(deal.updatedAt || deal.createdAt)}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                    <span
                        className="crm-badge"
                        style={getDaysColor()}
                    >
                        {daysInStage}d
                    </span>
                    <span
                        className="crm-badge"
                        style={{ background: 'var(--crm-primary-light)', color: 'var(--crm-primary)' }}
                    >
                        {deal.probability || 0}%
                    </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DealCard;
